import { useMemo } from 'react'
import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns'

import { Card } from '@/components/ui'
import { cn } from '@/lib/cn'
import { getAssignmentShift } from '../utils/assignmentUtils'
import { getCompanyColor } from '../utils/companyColor'
import { ShiftBlock } from './ShiftBlock'
import type { ShiftViewMode } from '../utils/dateRange'
import type { CompanyScopedAssignment } from '../hooks/useShiftsAcrossCompanies'

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

export interface MyShiftsCalendarProps {
  viewMode: ShiftViewMode
  /** The date the calendar is centred on; drives which month/week/day is shown. */
  focusedDate: Date
  assignments: CompanyScopedAssignment[]
  /** companyId -> display name, used for the small company badge in each shift. */
  companyNameById: Map<string, string>
  /** Assignment to visually highlight (e.g. when arriving from a shift notification). */
  highlightAssignmentId?: string
  /** Called when a shift is clicked (e.g. to confirm or inspect). */
  onSelect?: (assignment: CompanyScopedAssignment) => void
}

/** Inclusive list of dates between two dates (as Date objects). */
function eachDay(start: Date, end: Date): Date[] {
  const out: Date[] = []
  let d = start
  while (d <= end) {
    out.push(d)
    d = addDays(d, 1)
  }
  return out
}

/** Groups assignments by their yyyy-MM-dd date, sorted by start time within each day. */
function groupByDate(
  assignments: CompanyScopedAssignment[]
): Map<string, CompanyScopedAssignment[]> {
  const out = new Map<string, CompanyScopedAssignment[]>()
  for (const a of assignments) {
    // a.date may arrive as a full ISO datetime (e.g. 2026-06-14T00:00:00Z);
    // normalize to yyyy-MM-dd so it matches the grid cell keys.
    const key = a.date.length > 10 ? format(parseISO(a.date), 'yyyy-MM-dd') : a.date
    if (!out.has(key)) out.set(key, [])
    out.get(key)!.push(a)
  }
  for (const list of out.values()) {
    list.sort((x, y) => (x.startTime ?? '').localeCompare(y.startTime ?? ''))
  }
  return out
}

/** Renders the shift blocks for a single day. */
function DayShifts({
  items,
  companyNameById,
  highlightAssignmentId,
  onSelect,
  emptyHint,
}: {
  items: CompanyScopedAssignment[]
  companyNameById: Map<string, string>
  highlightAssignmentId?: string
  onSelect?: (assignment: CompanyScopedAssignment) => void
  emptyHint?: string
}): React.ReactElement {
  if (items.length === 0) {
    return emptyHint ? (
      <p className="px-1 py-2 text-center text-[11px] text-gray-400 dark:text-gray-500">
        {emptyHint}
      </p>
    ) : (
      <></>
    )
  }
  return (
    <div className="flex flex-col gap-1">
      {items.map((a) => {
        const shift = getAssignmentShift(a, new Map())
        const isHighlighted = a._id === highlightAssignmentId
        return (
          <div
            key={a._id}
            id={`shift-assignment-${a._id}`}
            className={cn(
              isHighlighted &&
                'rounded-md ring-2 ring-primary ring-offset-1 dark:ring-offset-gray-900'
            )}
          >
            <ShiftBlock
              assignment={a}
              shift={shift}
              companyId={a.companyId}
              companyName={companyNameById.get(a.companyId)}
              variant="monthly"
              onClick={() => onSelect?.(a)}
            />
          </div>
        )
      })}
    </div>
  )
}

function MonthlyView({
  focusedDate,
  byDate,
  companyNameById,
  highlightAssignmentId,
  onSelect,
}: {
  focusedDate: Date
  byDate: Map<string, CompanyScopedAssignment[]>
  companyNameById: Map<string, string>
  highlightAssignmentId?: string
  onSelect?: (assignment: CompanyScopedAssignment) => void
}): React.ReactElement {
  const gridStart = startOfWeek(startOfMonth(focusedDate), { weekStartsOn: 1 })
  const gridEnd = endOfWeek(endOfMonth(focusedDate), { weekStartsOn: 1 })
  const days = eachDay(gridStart, gridEnd)
  const today = new Date()

  return (
    <div>
      <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700">
        {WEEKDAY_LABELS.map((label) => (
          <div
            key={label}
            className="px-2 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
          >
            {label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd')
          const items = byDate.get(key) ?? []
          const inMonth = isSameMonth(day, focusedDate)
          const isToday = isSameDay(day, today)
          const isWeekend = day.getDay() === 0 || day.getDay() === 6
          return (
            <div
              key={key}
              className={cn(
                'min-h-[110px] border-b border-r border-gray-100 p-1.5 dark:border-gray-700/60',
                !inMonth && 'bg-gray-50/60 dark:bg-gray-900/30',
                isWeekend && inMonth && 'bg-gray-50/40 dark:bg-gray-800/20'
              )}
            >
              <div className="mb-1 flex items-center justify-between">
                <span
                  className={cn(
                    'inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-medium',
                    isToday
                      ? 'bg-primary text-white'
                      : inMonth
                        ? 'text-gray-700 dark:text-gray-100'
                        : 'text-gray-400 dark:text-gray-500'
                  )}
                >
                  {format(day, 'd')}
                </span>
                {items.length > 0 && (
                  <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500">
                    {items.length}
                  </span>
                )}
              </div>
              <DayShifts
                items={items}
                companyNameById={companyNameById}
                highlightAssignmentId={highlightAssignmentId}
                onSelect={onSelect}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

function WeeklyView({
  focusedDate,
  byDate,
  companyNameById,
  highlightAssignmentId,
  onSelect,
}: {
  focusedDate: Date
  byDate: Map<string, CompanyScopedAssignment[]>
  companyNameById: Map<string, string>
  highlightAssignmentId?: string
  onSelect?: (assignment: CompanyScopedAssignment) => void
}): React.ReactElement {
  const start = startOfWeek(focusedDate, { weekStartsOn: 1 })
  const days = eachDay(start, endOfWeek(focusedDate, { weekStartsOn: 1 }))
  const today = new Date()

  return (
    <div className="grid grid-cols-1 divide-y divide-gray-100 sm:grid-cols-7 sm:divide-y-0 sm:divide-x dark:divide-gray-700/60">
      {days.map((day) => {
        const key = format(day, 'yyyy-MM-dd')
        const items = byDate.get(key) ?? []
        const isToday = isSameDay(day, today)
        return (
          <div key={key} className="min-h-[140px] p-2">
            <div className="mb-2 flex items-center gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {format(day, 'EEE')}
              </span>
              <span
                className={cn(
                  'inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-medium',
                  isToday
                    ? 'bg-primary text-white'
                    : 'text-gray-700 dark:text-gray-100'
                )}
              >
                {format(day, 'd')}
              </span>
            </div>
            <DayShifts
              items={items}
              companyNameById={companyNameById}
              highlightAssignmentId={highlightAssignmentId}
              onSelect={onSelect}
              emptyHint="—"
            />
          </div>
        )
      })}
    </div>
  )
}

function DailyView({
  focusedDate,
  byDate,
  companyNameById,
  highlightAssignmentId,
  onSelect,
}: {
  focusedDate: Date
  byDate: Map<string, CompanyScopedAssignment[]>
  companyNameById: Map<string, string>
  highlightAssignmentId?: string
  onSelect?: (assignment: CompanyScopedAssignment) => void
}): React.ReactElement {
  const key = format(focusedDate, 'yyyy-MM-dd')
  const items = byDate.get(key) ?? []
  return (
    <div className="p-4">
      <div className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-100">
        {format(focusedDate, 'EEEE, d MMMM yyyy')}
      </div>
      {items.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
          No shifts on this day.
        </p>
      ) : (
        <div className="space-y-1.5">
          {items.map((a) => {
            const shift = getAssignmentShift(a, new Map())
            const cColor = getCompanyColor(a.companyId)
            const isHighlighted = a._id === highlightAssignmentId
            return (
              <div
                key={a._id}
                id={`shift-assignment-${a._id}`}
                className={cn(
                  'flex items-center gap-3 rounded-md border border-gray-100 p-1.5 dark:border-gray-700/60',
                  isHighlighted &&
                    'ring-2 ring-primary ring-offset-1 dark:ring-offset-gray-900'
                )}
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: cColor.dot }}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <ShiftBlock
                    assignment={a}
                    shift={shift}
                    companyId={a.companyId}
                    companyName={companyNameById.get(a.companyId)}
                    onClick={() => onSelect?.(a)}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Calendar view of the current user's assigned shifts. Renders monthly / weekly /
 * daily layouts using the same ShiftBlock visuals as the admin planning grid so the
 * two surfaces stay consistent. The parent owns date navigation + data fetching and
 * passes the already-fetched assignments for the visible range.
 */
export function MyShiftsCalendar({
  viewMode,
  focusedDate,
  assignments,
  companyNameById,
  highlightAssignmentId,
  onSelect,
}: MyShiftsCalendarProps): React.ReactElement {
  const byDate = useMemo(() => groupByDate(assignments), [assignments])

  return (
    <Card contentClassName="p-0">
      <div className="overflow-x-auto">
        {viewMode === 'monthly' ? (
          <MonthlyView
            focusedDate={focusedDate}
            byDate={byDate}
            companyNameById={companyNameById}
            highlightAssignmentId={highlightAssignmentId}
            onSelect={onSelect}
          />
        ) : viewMode === 'weekly' ? (
          <WeeklyView
            focusedDate={focusedDate}
            byDate={byDate}
            companyNameById={companyNameById}
            highlightAssignmentId={highlightAssignmentId}
            onSelect={onSelect}
          />
        ) : (
          <DailyView
            focusedDate={focusedDate}
            byDate={byDate}
            companyNameById={companyNameById}
            highlightAssignmentId={highlightAssignmentId}
            onSelect={onSelect}
          />
        )}
      </div>
    </Card>
  )
}

MyShiftsCalendar.displayName = 'MyShiftsCalendar'
